import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, CircleCheck, TriangleAlert } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { usePushSubscription } from "@/core/hooks/usePushSubscription";
import {
  PEDIDO_VAZIO,
  abertoNaTelaDeInicio,
  adiarPedido,
  chavePedido,
  devePedirAgora,
  gravarPedido,
  iosForaDaTelaDeInicio,
  lerPedido,
  type PedidoPermissao,
} from "@/core/notificacoes/permissao";

/** localStorage pode lançar erro só de ser acessado (janela privativa, dados bloqueados). */
function obterStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const CARTAO_CLASSE =
  "mb-6 flex flex-col gap-4 rounded-2xl border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center sm:p-5";
const BOTAO_SECUNDARIO =
  "min-h-11 rounded-lg border border-border bg-surface px-4 text-sm font-bold text-muted transition-colors hover:bg-surface-2 hover:text-text";

/**
 * Cartão "Receber avisos?" do Início (aprovado em 02/11/2026, protótipo "Pedir permissão").
 *
 * Só convida. A janela de permissão do navegador abre no toque em "Ativar notificações" (os
 * navegadores exigem um toque da pessoa, e a decisão de lifecycle de 03/09/2026 proíbe ativar
 * sozinho). Regras (permissao.ts): só aparece para quem ainda não decidiu neste aparelho; "Agora não"
 * esconde e volta depois de 7 dias, no máximo 2 vezes. Quem bloqueou não é cobrado de novo; a
 * explicação de como liberar fica em Configurações de notificações. No iPhone fora da Tela de Início o
 * navegador não recebe notificação, então o cartão ensina a instalar. O mesmo cartão vale na primeira
 * abertura do app instalado.
 */
export function CartaoPermissaoNotificacao() {
  const navigate = useNavigate();
  const { pessoa } = useAuth();
  const pessoaId = pessoa?.id ?? null;
  const { estado, processando, ativar } = usePushSubscription();

  // `de` diz de quem é a lembrança lida; antes de ler o aparelho o cartão não aparece (evita piscar).
  const [pedido, setPedido] = useState<{ de: string | null; valor: PedidoPermissao }>({
    de: null,
    valor: PEDIDO_VAZIO,
  });
  const [tocou, setTocou] = useState(false);

  useEffect(() => {
    setPedido(
      pessoaId
        ? { de: pessoaId, valor: lerPedido(obterStorage(), chavePedido(pessoaId)) }
        : { de: null, valor: PEDIDO_VAZIO },
    );
  }, [pessoaId]);

  const adiar = useCallback(() => {
    if (!pessoaId) return;
    // Relê antes de gravar: outra aba pode ter mudado a lembrança.
    const chave = chavePedido(pessoaId);
    const novo = adiarPedido(lerPedido(obterStorage(), chave), Date.now());
    gravarPedido(obterStorage(), chave, novo);
    setPedido({ de: pessoaId, valor: novo });
  }, [pessoaId]);

  const permissao: NotificationPermission =
    typeof Notification === "undefined" ? "denied" : Notification.permission;

  // Resultado do toque em "Ativar": some sozinho depois de alguns segundos.
  const resultado = (() => {
    if (!tocou || processando) return null;
    if (estado.tipo === "pronto" && estado.inscrito) return "ativado" as const;
    if (estado.tipo === "erro") return "erro" as const;
    if (permissao === "denied") return "bloqueado" as const;
    return null;
  })();
  useEffect(() => {
    if (!resultado) return;
    const t = window.setTimeout(() => setTocou(false), 6000);
    return () => window.clearTimeout(t);
  }, [resultado]);

  async function aoAtivar() {
    setTocou(true);
    await ativar();
  }

  if (resultado === "ativado") {
    return (
      <section aria-label="Avisos no aparelho" role="status" className={CARTAO_CLASSE}>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <CircleCheck size={22} aria-hidden="true" />
        </span>
        <p className="min-w-0 flex-1 text-sm font-bold text-text">
          Pronto! Notificações ativadas neste aparelho.
        </p>
      </section>
    );
  }
  if (resultado === "bloqueado" || resultado === "erro") {
    return (
      <section aria-label="Avisos no aparelho" role="status" className={CARTAO_CLASSE}>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-warning/10 text-warning">
          {resultado === "erro" ? (
            <TriangleAlert size={22} aria-hidden="true" />
          ) : (
            <BellOff size={22} aria-hidden="true" />
          )}
        </span>
        <p className="min-w-0 flex-1 text-sm text-text">
          {resultado === "erro" && estado.tipo === "erro"
            ? estado.mensagem
            : "Notificações bloqueadas. Para receber avisos depois, veja como liberar em Configurações de notificações."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/conta/notificacoes")}
          className={BOTAO_SECUNDARIO}
        >
          Ver como liberar
        </button>
      </section>
    );
  }

  if (pedido.de !== pessoaId || pessoaId === null || !devePedirAgora(pedido.valor, Date.now())) {
    return null;
  }

  // iPhone/iPad fora da Tela de Início: o Safari não recebe notificação. Ensina a instalar.
  const ios =
    estado.tipo === "nao_suportado" &&
    iosForaDaTelaDeInicio(navigator.userAgent, abertoNaTelaDeInicio());
  if (ios) {
    return (
      <section aria-label="Avisos no iPhone" className={CARTAO_CLASSE}>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <Bell size={22} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold text-text">Quer receber avisos no iPhone?</p>
          <p className="text-sm text-muted">
            No iPhone, as notificações só funcionam com o Elos instalado na Tela de Início.
          </p>
          <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-sm text-text">
            <li>Toque em Compartilhar, na barra do Safari.</li>
            <li>Escolha &quot;Adicionar à Tela de Início&quot;.</li>
            <li>Abra o Elos pelo novo ícone. Aí pedimos a permissão.</li>
          </ol>
        </div>
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={adiar} className={BOTAO_SECUNDARIO}>
            Agora não
          </button>
        </div>
      </section>
    );
  }

  // Só quem ainda não decidiu: permissão "default" e sem assinatura ativa. Já bloqueou ou já
  // ativou (ou desativou de propósito nas configurações) não é cobrado de novo.
  if (estado.tipo !== "pronto" || estado.inscrito || permissao !== "default") return null;

  return (
    <section aria-label="Avisos no aparelho" className={CARTAO_CLASSE}>
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
        <Bell size={22} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-base font-bold text-text">Receber avisos neste aparelho?</p>
        <p className="text-sm text-muted">Ative para ser avisado mesmo com o Elos fechado.</p>
        <ul className="mt-2 space-y-0.5 text-sm text-text">
          <li>✓ Pedido ou empréstimo aprovado</li>
          <li>✓ Convite para ministério ou escala</li>
          <li>✓ Comunicados da igreja</li>
        </ul>
      </div>
      <div className="flex shrink-0 gap-2">
        <button type="button" onClick={adiar} disabled={processando} className={BOTAO_SECUNDARIO}>
          Agora não
        </button>
        <button
          type="button"
          onClick={() => void aoAtivar()}
          disabled={processando}
          className="min-h-11 rounded-lg bg-primary px-4 text-sm font-bold text-on-primary transition-colors hover:bg-primary-hover disabled:opacity-60"
        >
          {processando ? "Ativando..." : "Ativar notificações"}
        </button>
      </div>
    </section>
  );
}
